// src/lib/advisorConnectionStatus.js
//
// Resolves a result session's advisor relationship as one of three states.
// Owned by ResultCTA itself (see ResultCTA.js) rather than by each result
// page — previously TriageResults.js and CalculatorResult.js each fetched
// this independently and only one ever got the post-submit refetch wiring.
// Centralizing the fetch means no result page has to implement it at all.
//
// BUG FIX (Sept 2026): this used to return a boolean — "does the session's
// tax_subject have a partner_id?" — and ResultCTA rendered true as "✓ You're
// connected with {partner}. They'll follow up directly." But partner_id is
// attribution, set silently by claim-session step 7 for any referred claim,
// with no consent shown. A referred user who saved via the plain claim flow
// was told they were connected, lost the "Send to {partner}" button (the
// only consent action), and the advisor saw only a masked referral
// placeholder. Attribution ≠ access (partner_referral_framework.md). Now
// three states, resolved server-side by get-advisor-connection-status via
// hasPartnerDataConsent() — the single implementation of the consent read:
//
//   none      — no claimed subject / no partner assigned
//   referred  — partner assigned, no partner_data_share consent yet
//               → UI must still offer "Send to {partner}"
//   connected — partner assigned AND consent on record
//               → the advisor sees a full lead; UI shows the banner
//
// ADVISOR_CONNECTION is mirrored by hand in the edge function's STATUS
// constant (different runtime). Change both together. isDefaultPartner is
// mirrored the same way against the edge function's is_default_partner.
//
// Never called without a real user session: an anonymous visitor can't own
// a claimed session, so their status is always 'none' — no request is made,
// and the edge function can require a real user JWT (standard deploy).
//
// Lookup key follows the draft/publish rule used by claim-session and
// advisor-connect: prefer public_token, fall back to the internal session
// id for an unpublished draft (which never has a public_token).

import { supabase } from './supabase'

export const ADVISOR_CONNECTION = Object.freeze({
  NONE:      'none',
  REFERRED:  'referred',
  CONNECTED: 'connected',
})

const KNOWN_STATUSES = Object.values(ADVISOR_CONNECTION)

export const NO_ADVISOR_CONNECTION = Object.freeze({
  status:          ADVISOR_CONNECTION.NONE,
  partnerName:     null,
  isDefaultPartner: false,
})

// product: 'calculator' | 'triage'
// publicToken: may be null for an unpublished draft.
// sessionId: internal id — fallback lookup key when publicToken isn't set.
// Returns { status, partnerName }. Never throws — any failure resolves to
// NO_ADVISOR_CONNECTION, which renders the default (actionable) CTA. That
// failure direction is safe: at worst a connected user sees the "Send"
// button again, and advisor-connect already handles a repeat submission
// idempotently.
export async function getAdvisorConnectionStatus(product, publicToken, sessionId = null) {
  if (!publicToken && !sessionId) return NO_ADVISOR_CONNECTION

  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return NO_ADVISOR_CONNECTION

    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-advisor-connection-status`,
      {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          product,
          public_token: publicToken ?? null,
          session_id:   sessionId ?? null,
        }),
      },
    )
    if (!res.ok) return NO_ADVISOR_CONNECTION

    const data = await res.json()
    return {
      status:           KNOWN_STATUSES.includes(data?.status) ? data.status : ADVISOR_CONNECTION.NONE,
      partnerName:      data?.partner_name ?? null,
      isDefaultPartner: Boolean(data?.is_default_partner),
    }
  } catch (err) {
    console.error('getAdvisorConnectionStatus error:', err)
    return NO_ADVISOR_CONNECTION
  }
}